import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Divider,
  Tag,
  Tooltip,
  Typography,
} from "@suraasa/placebo-ui"
import { LearningItemType } from "api/resources/learningItems/types"
import CheckRestrictions from "features/AccessManagement/CheckRestrictions"
import { NavArrowDown } from "iconoir-react"
import { formatDate } from "utils/helpers"

import LiveAssessmentCard from "./LiveAssessmentCard"
import {
  findAssessmentSchedule,
  findClassSession,
  isAssessmentUpcoming,
  useUpcomingPlannerItems,
} from "./useUpcomingPlannerItems"

const timeFormat = "do MMMM yyyy, h:mm a"

const getSessionDescription = (props: {
  professorName: string
  time: string
}) => {
  return [
    formatDate(props.time, timeFormat),
    props.professorName ? `Conducted by ${props.professorName}` : null,
  ]
    .filter(Boolean)
    .join(" • ")
}

const NextUp = ({
  learningItemType,
  learningItemId,
}: {
  learningItemType: LearningItemType
  learningItemId: number
}) => {
  const { isSuccess, items } = useUpcomingPlannerItems({
    learningItemType,
    learningItemId,
  })

  if (!isSuccess || items.length === 0) return null

  const assessmentSchedule = findAssessmentSchedule(items)
  // Only genuinely upcoming (not yet completed/missed) assessments belong
  // here — completed/missed assessments aren't shown anywhere on the
  // Course/Qualification overview currently.
  const upcomingAssessment =
    assessmentSchedule && isAssessmentUpcoming(assessmentSchedule)
      ? assessmentSchedule
      : undefined
  const session = findClassSession(items)

  if (!upcomingAssessment && !session) return null

  return (
    <div>
      <Typography variant="subtitle2" className="mb-1.5 font-semibold">
        Upcoming
      </Typography>

      <div className="flex flex-col rounded-2xl border border-onSurface-200">
        {upcomingAssessment && (
          <LiveAssessmentCard assessmentSchedule={upcomingAssessment} />
        )}

        {upcomingAssessment && session && (
          <Divider className="bg-onSurface-200" />
        )}

        {session && (
          <CheckRestrictions
            accessStatus={session.accessStatus}
            render={({ isRestricted }) => {
              const canJoin = session.videoMeeting?.joiningUrl

              return (
                <div className="flex flex-col gap-2 p-2.5">
                  <div className="flex size-4 shrink-0 items-center justify-center rounded-full bg-primary-500">
                    <div className="flex size-2.25 items-center justify-center rounded-full bg-primary-300">
                      <div className="size-1.25 rounded-full bg-white" />
                    </div>
                  </div>

                  <div>
                    <Typography
                      variant="title4"
                      className="mb-0.5  text-onSurface-800"
                    >
                      {session.classId.name}
                    </Typography>
                    <Typography
                      className="text-onSurface-500"
                      variant="smallBody"
                    >
                      {getSessionDescription({
                        professorName: session.professor.fullName,
                        time: session.startTime,
                      })}
                    </Typography>
                    {session.lectures.length > 0 && (
                      <Accordion type="single" collapsible className="mt-1">
                        <AccordionItem value="topics-covered">
                          <AccordionTrigger
                            icon={<NavArrowDown color="black" />}
                            className="items-center justify-start gap-0.5 p-0 text-strongSmallBody"
                          >
                            Topics Covered
                          </AccordionTrigger>
                          <AccordionContent className="mt-1 px-0">
                            <div className="flex flex-wrap gap-1">
                              {session.lectures.map(lecture => (
                                <Tag
                                  key={lecture.uuid}
                                  color="onSurface"
                                  className="border-onSurface-300"
                                >
                                  {lecture.name}
                                </Tag>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    )}
                  </div>

                  <div className="w-full sm:w-fit">
                    <Tooltip
                      enabled={isRestricted || !canJoin}
                      content={
                        isRestricted
                          ? "Access Restricted"
                          : !canJoin
                          ? `Starts at ${formatDate(
                              session.startTime,
                              timeFormat
                            )}`
                          : ""
                      }
                    >
                      <Button
                        color="primary"
                        className="w-full"
                        size="sm"
                        disabled={isRestricted || !canJoin}
                        onClick={() => {
                          if (session.videoMeeting?.joiningUrl)
                            window.open(
                              session.videoMeeting.joiningUrl,
                              "_blank"
                            )
                        }}
                      >
                        Join Class
                      </Button>
                    </Tooltip>
                  </div>
                </div>
              )
            }}
          />
        )}
      </div>
    </div>
  )
}

export default NextUp
